import { useState, useEffect } from "react";
import axios from "axios";
import { API, USER_ID } from "../App";
import { Plus, TrendUp, TrendDown, Minus, Target, Calendar } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";

const Progress = ({ profile }) => {
  const [weightHistory, setWeightHistory] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newWeight, setNewWeight] = useState(profile?.weight || 75);
  const [weightDate, setWeightDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchWeightHistory(); }, []);

  const fetchWeightHistory = async () => {
    try {
      const response = await axios.get(`${API}/weight/${USER_ID}?limit=90`);
      setWeightHistory(response.data.reverse());
    } catch (error) { console.error("Error:", error); }
    finally { setLoading(false); }
  };

  const handleAddWeight = async () => {
    try {
      await axios.post(`${API}/weight`, { user_id: USER_ID, date: weightDate, weight: newWeight, notes });
      toast.success("Poids enregistré !");
      setShowAddModal(false);
      setNotes("");
      fetchWeightHistory();
    } catch (error) { toast.error("Erreur"); }
  };

  const currentWeight = weightHistory.length > 0 ? weightHistory[weightHistory.length - 1]?.weight : profile?.weight;
  const goalWeight = profile?.goal_weight;
  const weightChange = weightHistory.length >= 2 ? (weightHistory[weightHistory.length - 1]?.weight - weightHistory[0]?.weight).toFixed(1) : 0;
  const progressToGoal = goalWeight && currentWeight && profile?.weight ? ((profile.weight - currentWeight) / (profile.weight - goalWeight) * 100).toFixed(0) : 0;

  return (
    <div className="p-4 md:p-8 animate-fadeIn max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl uppercase tracking-tight text-charcoal">Progression</h1>
        <p className="text-gray-500">Visualise ton évolution, garde le cap et vois ce qui change semaine après semaine.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4" data-testid="current-weight">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Poids actuel</p>
          <p className="text-2xl font-bold text-charcoal">{currentWeight || '--'} <span className="text-sm font-normal text-gray-400">kg</span></p>
        </div>
        <div className="card p-4" data-testid="goal-weight">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Objectif</p>
          <p className="text-2xl font-bold text-gold">{goalWeight || '--'} <span className="text-sm font-normal text-gray-400">kg</span></p>
        </div>
        <div className="card p-4" data-testid="weight-change">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Évolution</p>
          <div className="flex items-center gap-2">
            {Number(weightChange) < 0 ? <TrendDown size={20} className="text-green-500" /> : Number(weightChange) > 0 ? <TrendUp size={20} className="text-red-500" /> : <Minus size={20} className="text-gray-400" />}
            <p className={`text-2xl font-bold ${Number(weightChange) < 0 ? 'text-green-500' : Number(weightChange) > 0 ? 'text-red-500' : 'text-charcoal'}`}>
              {weightChange > 0 ? '+' : ''}{weightChange} kg
            </p>
          </div>
        </div>
        <div className="card p-4" data-testid="progress-goal">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Progression</p>
          <p className="text-2xl font-bold text-charcoal">{progressToGoal > 0 ? progressToGoal : 0}%</p>
          <div className="progress-bar mt-2"><div className="progress-bar-fill" style={{ width: `${Math.min(Math.max(progressToGoal, 0), 100)}%` }} /></div>
        </div>
      </div>

      <div className="card p-5 mb-6" data-testid="weight-chart">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-charcoal">Historique</h2>
          <button onClick={() => setShowAddModal(true)} className="btn-primary text-sm py-2" data-testid="add-weight-button">
            <Plus size={18} className="mr-1" /> Ajouter
          </button>
        </div>
        {weightHistory.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center">
            <Target size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-400 mb-4">Aucune donnée</p>
            <button onClick={() => setShowAddModal(true)} className="btn-primary" data-testid="add-first-weight">Enregistrer mon poids</button>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightHistory} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" stroke="#9CA3AF" fontSize={10} tickFormatter={(v) => new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} />
                <YAxis stroke="#9CA3AF" fontSize={10} domain={['dataMin - 2', 'dataMax + 2']} />
                <Tooltip contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '12px' }} labelFormatter={(v) => new Date(v).toLocaleDateString('fr-FR')} formatter={(v) => [`${v} kg`, 'Poids']} />
                {goalWeight && <ReferenceLine y={goalWeight} stroke="#F5B83D" strokeDasharray="5 5" />}
                <Line type="monotone" dataKey="weight" stroke="#F5B83D" strokeWidth={2} dot={{ fill: '#F5B83D', strokeWidth: 0, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {weightHistory.length > 0 && (
        <div className="card overflow-hidden" data-testid="weight-history-list">
          <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-charcoal">Historique récent</h3></div>
          <div className="divide-y divide-gray-100">
            {weightHistory.slice(-10).reverse().map((entry, idx) => (
              <div key={entry.id || idx} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-gray-400" />
                  <div>
                    <p className="text-charcoal">{new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                    {entry.notes && <p className="text-xs text-gray-500">{entry.notes}</p>}
                  </div>
                </div>
                <p className="font-bold text-gold">{entry.weight} kg</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-weight-modal">
            <div className="p-5 border-b border-gray-100"><h2 className="font-display text-xl text-charcoal">Enregistrer mon poids</h2></div>
            <div className="p-5">
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Date</label>
                <input type="date" value={weightDate} onChange={(e) => setWeightDate(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="weight-date-input" />
              </div>
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Poids (kg)</label>
                <input type="number" step="0.1" value={newWeight} onChange={(e) => setNewWeight(Number(e.target.value))} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-2xl font-bold text-center" data-testid="weight-value-input" />
              </div>
              <div className="mb-6">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Notes</label>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: À jeun" className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="weight-notes-input" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowAddModal(false)} className="flex-1 btn-secondary" data-testid="cancel-weight">Annuler</button>
                <button onClick={handleAddWeight} className="flex-1 btn-primary" data-testid="confirm-weight">Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Progress;
